/**
 * business_setup_controller.js — account / business-setup page behaviour (Stimulus).
 *
 * Scoped to the wrapper around the whole account page (personal-details + password
 * inline editors, the create-business form's country/VAT visibility, and the
 * delete-account modal's confirm guard). Replaces an external business_setup.js plus
 * an end-of-body inline <script> that used getElementById + addEventListener +
 * DOMContentLoaded/turbo:load (none of which survive a Turbo <body> swap cleanly).
 *
 * Behaviours ported verbatim:
 *   - Personal details: edit -> show form, cancel -> revert, submit -> window.api POST.
 *   - Password: edit/cancel toggles, submit -> window.api POST with inline error box.
 *   - Create-business modal (templates/auth/business_setup.html): region change
 *     relabels the tax question; the checkbox
 *     shows/hides the VAT-number field (region list read from data-vat-regions, SSOT).
 *   - Delete-account modal: confirm button stays disabled until the user retypes their
 *     own email (read from data-user-email on the form).
 *
 * The form/link POSTs that navigate into the authenticated shell keep data-turbo="false"
 * on their elements; the AJAX editors here post via window.api and refresh in place
 * (Turbo.visit replace, falling back to location.reload).
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        // ---- Personal details ------------------------------------------------
        editDetails() {
            var display = this._q('#detailsDisplay');
            var editBtn = this._q('#editDetailsBtn');
            var form = this._q('#personalDetailsForm');
            if (!form) return;
            if (display) display.classList.add('d-none');
            if (editBtn) editBtn.classList.add('d-none');
            form.classList.remove('d-none');
        }

        cancelEditDetails() {
            var display = this._q('#detailsDisplay');
            var editBtn = this._q('#editDetailsBtn');
            var form = this._q('#personalDetailsForm');
            if (!form) return;
            form.classList.add('d-none');
            if (display) display.classList.remove('d-none');
            if (editBtn) editBtn.classList.remove('d-none');
        }

        submitDetails(event) {
            event.preventDefault();
            var form = this._q('#personalDetailsForm');
            var btn = this._q('#savePersonalDetails');
            if (!form) return;
            if (btn) btn.disabled = true;

            window.api(form.dataset.submitUrl, {
                method: 'POST',
                body: new URLSearchParams(new FormData(form)),
                notifyError: false,
            })
                .then(function (data) {
                    if (btn) btn.disabled = false;
                    if (data && data.success) {
                        if (window.showNotification) window.showNotification('Details saved', 'success');
                        if (window.Turbo) Turbo.visit(window.location.href, { action: 'replace' });
                        else location.reload();
                    } else {
                        if (window.showNotification) window.showNotification((data && data.error) || 'Save failed', 'error');
                    }
                })
                .catch(function (err) {
                    if (btn) btn.disabled = false;
                    var msg = (err && err.data && err.data.error) || 'Network error';
                    if (window.showNotification) window.showNotification(msg, 'error');
                });
        }

        // ---- Password --------------------------------------------------------
        editPassword() {
            var display = this._q('#passwordDisplay');
            var editBtn = this._q('#editPasswordBtn');
            var form = this._q('#passwordForm');
            var error = this._q('#passwordError');
            if (!form) return;
            if (display) display.classList.add('d-none');
            if (editBtn) editBtn.classList.add('d-none');
            form.classList.remove('d-none');
            if (error) error.classList.add('d-none');
        }

        cancelEditPassword() {
            var display = this._q('#passwordDisplay');
            var editBtn = this._q('#editPasswordBtn');
            var form = this._q('#passwordForm');
            var error = this._q('#passwordError');
            if (!form) return;
            form.classList.add('d-none');
            form.reset();
            if (display) display.classList.remove('d-none');
            if (editBtn) editBtn.classList.remove('d-none');
            if (error) error.classList.add('d-none');
        }

        submitPassword(event) {
            event.preventDefault();
            var form = this._q('#passwordForm');
            var btn = this._q('#savePassword');
            var error = this._q('#passwordError');
            if (!form) return;
            if (error) error.classList.add('d-none');
            if (btn) btn.disabled = true;

            window.api(form.dataset.submitUrl, {
                method: 'POST',
                body: new URLSearchParams(new FormData(form)),
                notifyError: false,
            })
                .then(function (data) {
                    if (btn) btn.disabled = false;
                    if (data && data.success) {
                        if (window.showNotification) window.showNotification('Password saved', 'success');
                        if (window.Turbo) Turbo.visit(window.location.href, { action: 'replace' });
                        else location.reload();
                    } else {
                        if (error) {
                            error.textContent = (data && data.error) || 'Save failed';
                            error.classList.remove('d-none');
                        }
                    }
                })
                .catch(function (err) {
                    if (btn) btn.disabled = false;
                    if (error) {
                        error.textContent = (err && err.data && err.data.error) || 'Network error';
                        error.classList.remove('d-none');
                    }
                });
        }

        // ---- Create-business form: "Registered for GST/VAT?" ------------------
        // Every region asks; the label names the region's tax and the VAT number
        // only applies to a registered business in a VAT region.
        onRegionChange() {
            var select = this._q('#business_region');
            var label = this._q('#tax_registered_label');
            var taxNames = JSON.parse(select.dataset.taxNames || '{}');
            if (label) label.textContent = 'Registered for ' + (taxNames[select.value] || 'tax');
            this._syncVatNumber();
        }

        onRegistrationChange() {
            this._syncVatNumber();
        }

        _syncVatNumber() {
            var select = this._q('#business_region');
            var checkbox = this._q('#tax_registered');
            var vatRegions = (select.dataset.vatRegions || '').split(',').filter(Boolean);
            this._showVatNumber(vatRegions.indexOf(select.value) !== -1 && !!(checkbox && checkbox.checked));
        }

        _showVatNumber(show) {
            var vatNumberField = this._q('#vat_number_field');
            var vatNumberInput = this._q('#vat_number');
            if (vatNumberField) vatNumberField.classList.toggle('d-none', !show);
            if (!show && vatNumberInput) vatNumberInput.value = '';
        }

        // ---- Delete-account modal: confirm guard -----------------------------
        onDeleteConfirmInput(event) {
            var form = this._q('#deleteAccountForm');
            var confirmBtn = this._q('#confirmDeleteAccountBtn');
            if (!form || !confirmBtn) return;
            var expected = (form.dataset.userEmail || '').trim().toLowerCase();
            confirmBtn.disabled = event.currentTarget.value.trim().toLowerCase() !== expected;
        }

        _q(selector) { return this.element.querySelector(selector); }
}
